import { memo } from 'react'

import { Group, Stack, Title } from '@mantine/core'

import { usdFormatter } from '@repo/common/helpers/formatters'
import { useAllFundInfo } from '@repo/common/queries/funds'
import { type AllFundSymbol } from '@repo/common/queries/products'

import { LinkButton } from '@/components/LinkButton'
import { FundNav1yAPY } from '@/components/updating_numbers/FundNav1yAPY'
import { FundPrice } from '@/components/updating_numbers/FundPrice'
import { FundTVL } from '@/components/updating_numbers/FundTVL'
import { useCompanyMetrics } from '@/queries/company'

import { CompanyLogo } from '../CompanyLogo'
import { InvestmentRow } from '../InvestmentRow'

export const FundRow = memo<{
  symbol?: AllFundSymbol
  variant?: ReactProps<typeof InvestmentRow>['variant']
  w?: ReactProps<typeof InvestmentRow>['w']
  // eslint-disable-next-line max-lines-per-function, complexity
}>(function FundRow({ symbol, variant = 'invest', w }) {
  const { data: fund } = useAllFundInfo({ symbol })
  const metrics_data = useCompanyMetrics()

  const top_companies =
    // eslint-disable-next-line complexity
    fund?.companies?.filter((c) => {
      if (metrics_data.data == null) return false
      if (metrics_data.data[c.cms_name] == null) {
        if (symbol != 'TOKX') {
          console.error(`No metrics data for ${c.cms_name}`)
        }
        return false
      }
      if (metrics_data.data[c.cms_name].highlight == 'any') return true
      if (
        fund.image_slug.toLowerCase() !== 'technology' &&
        metrics_data.data[c.cms_name].highlight == 'direct'
      ) {
        return true
      }

      return false
    }) ?? []

  if (fund == null) return null

  return (
    <InvestmentRow
      symbol={fund.symbol}
      name={fund.name}
      price_per_token={<FundPrice symbol={fund.symbol} />}
      nav_per_token={usdFormatter(fund.price_per_share)}
      one_year_apy={<FundNav1yAPY symbol={fund.symbol} />}
      tvl={<FundTVL symbol={fund.symbol} />}
      variant={variant}
      w={w}
    >
      {variant == 'invest' && (
        <Group gap="lg" style={{ alignSelf: 'flex-start' }}>
          <Stack gap="0">
            <Title order={2} fz="xs" lh="2">
              Companies including
            </Title>
            <Group gap="xs" wrap="nowrap">
              {top_companies.slice(0, 3).map(({ cms_name, name }) => (
                <CompanyLogo
                  key={cms_name}
                  cms_name={cms_name}
                  title={name}
                  h="30"
                />
              ))}
            </Group>
          </Stack>
        </Group>
      )}
      <Group flex="1" justify="flex-end" mr="lg">
        {variant != 'details' && (
          <LinkButton
            to="/investments/$symbol"
            params={{ symbol: fund.symbol }}
            size="sm"
            variant="outline"
          >
            View Details
          </LinkButton>
        )}
      </Group>
    </InvestmentRow>
  )
})
